import React, { useState, useEffect } from 'react';
import api from '@/auth/api';
import Loader from '@/components/Loader';
import { Doughnut, Pie, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Title
} from "chart.js";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartPie,
  faTable,
  faUndo,
  faFilter,
  faCalendarAlt,
  faCoins,
  faExchangeAlt,
  faGift,
  faUserPlus,
  faShareAlt,
  faRefresh,
  faClock,
  faEye,
  faEyeSlash,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';

// Register Chart.js components
ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Title
);

function Stats() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter States - mapping to backend parameter 'filter'
  const [filter, setFilter] = useState('this_month'); // Default to this month
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // UI View Mode: 'charts' | 'table'
  const [viewMode, setViewMode] = useState('charts');
  const [showCounts, setShowCounts] = useState(false);

  // Fetch Stats data from API
  const fetchStats = async (resetFilter = null) => {
    setLoading(true);
    setError(null);
    try {
      const activeFilter = resetFilter !== null ? resetFilter : filter;
      const params = {};

      if (activeFilter) {
        params.filter = activeFilter;
      }

      if (activeFilter === 'custom') {
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
      }

      const response = await api.get('/transaction/panel/transaction/stats', { params });

      if (response.data) {
        setData(response.data);
      } else {
        setError("Empty response received from the server.");
      }
    } catch (err) {
      console.error("Error fetching transaction stats:", err);
      setError(err.response?.data?.message || err.message || "Failed to load transaction stats.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  // Filter application handlers
  const handleApplyFilter = (e) => {
    e.preventDefault();
    fetchStats();
  };

  const handleResetFilter = () => {
    setFilter('this_month');
    setStartDate('');
    setEndDate('');
    fetchStats('this_month');
  };

  // Helper formatting functions
  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    return new Intl.NumberFormat().format(num);
  };

  const formatPoints = (points) => {
    if (points === undefined || points === null) return '0 pts';
    return formatNumber(points) + ' pts';
  };

  if (loading && !data) {
    return (
      <div className="w-full py-24 flex justify-center items-center">
        <Loader />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="w-full max-w-4xl mx-auto mt-8 p-6 bg-red-50 border border-red-200 rounded-xl text-center dark:bg-red-950/20 dark:border-red-900/30">
        <p className="text-red-700 dark:text-red-400 font-semibold mb-3">Error Loading Statistics</p>
        <p className="text-sm text-red-600 dark:text-red-500 mb-4">{error}</p>
        <button
          onClick={() => fetchStats()}
          className="activeBtn px-4 py-2 text-white rounded-lg text-sm hover:opacity-90 transition"
        >
          <FontAwesomeIcon icon={faRefresh} className="mr-2" />
          Retry
        </button>
      </div>
    );
  }

  // Destructure transactional data
  const { signup_POI = {}, transaction = {} } = data || {};
  const { Count = {}, Value = {}, title_wise = [], status_wise = [] } = transaction;

  // Chart 1 & 2 Configs: Category Distribution
  const categoryLabels = title_wise.map(item => item.title);
  const categoryCounts = title_wise.map(item => item.transaction_count);
  const categoryPoints = title_wise.map(item => item.total_points);

  const colors = [
    'rgba(35, 98, 159, 0.85)',   // Brand Light Blue
    'rgba(103, 169, 123, 0.85)',  // Brand Green
    'rgba(245, 158, 11, 0.85)',   // Amber
    'rgba(139, 92, 246, 0.85)',   // Purple
    'rgba(239, 68, 68, 0.85)',    // Red
    'rgba(6, 182, 212, 0.85)',    // Cyan
  ];

  const borderColors = [
    'rgba(35, 98, 159, 1)',
    'rgba(103, 169, 123, 1)',
    'rgba(245, 158, 11, 1)',
    'rgba(139, 92, 246, 1)',
    'rgba(239, 68, 68, 1)',
    'rgba(6, 182, 212, 1)',
  ];

  const countChartData = {
    labels: categoryLabels,
    datasets: [{
      label: 'Transaction Count',
      data: categoryCounts,
      backgroundColor: colors,
      borderColor: borderColors.map(c => c.replace('1)', '0.3)')),
      borderWidth: 1,
    }]
  };

  const valueChartData = {
    labels: categoryLabels,
    datasets: [{
      label: 'Points Transacted',
      data: categoryPoints,
      backgroundColor: colors.slice().reverse(), // Alternate sequence
      borderColor: borderColors.slice().reverse().map(c => c.replace('1)', '0.3)')),
      borderWidth: 1,
    }]
  };

  // Chart 3 & 4 Configs: Sign-up / Referral POIs
  const poiLabels = ['Welcome Bonus', 'Referrer Bonus', 'Refer Bonus'];
  const poiCounts = [
    signup_POI.welcome_bonus_count || 0,
    signup_POI.referrer_bonus_count || 0,
    signup_POI.refer_bonus_count || 0
  ];
  const poiPoints = [
    signup_POI.welcome_bonus_points || 0,
    signup_POI.referrer_bonus_points || 0,
    signup_POI.refer_bonus_points || 0
  ];

  const poiPointsChartData = {
    labels: poiLabels,
    datasets: [{
      label: 'Points Distributed',
      data: poiPoints,
      backgroundColor: 'rgba(139, 92, 246, 0.8)',
      borderColor: 'rgba(139, 92, 246, 1)',
      borderWidth: 1,
      borderRadius: 6,
    }]
  };

  const poiCountsChartData = {
    labels: poiLabels,
    datasets: [{
      label: 'Distribution Count',
      data: poiCounts,
      backgroundColor: 'rgba(6, 182, 212, 0.8)',
      borderColor: 'rgba(6, 182, 212, 1)',
      borderWidth: 1,
      borderRadius: 6,
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          font: { size: 11 },
          color: 'currentColor'
        }
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || '';
            if (label) label += ': ';
            if (context.parsed.y !== undefined) {
              label += typeof context.parsed.y === 'number' && context.parsed.y > 1000
                ? formatPoints(context.parsed.y)
                : formatNumber(context.parsed.y);
            } else if (context.parsed !== undefined) {
              label += context.dataset.label.includes('Points')
                ? formatPoints(context.parsed)
                : formatNumber(context.parsed);
            }
            return label;
          }
        }
      }
    }
  };

  const barChartOptions = {
    ...chartOptions,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return value >= 1000 ? formatPoints(value) : value;
          },
          font: { size: 10 },
          color: 'currentColor'
        },
        grid: {
          color: 'rgba(156, 163, 175, 0.1)'
        }
      },
      x: {
        ticks: {
          font: { size: 11 },
          color: 'currentColor'
        },
        grid: {
          display: false
        }
      }
    }
  };

  return (
    <div className="w-full max-w-[95%] mx-auto px-4 py-6 text-gray-800 dark:text-gray-200">

      {/* 1. Header Toolbar */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">Transaction Statistics</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Real-time breakdown of user rewards, redeem requests, and points circulation.</p>
          {data?.filter?.start_date && data?.filter?.end_date && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-50/50 text-blue-600 dark:bg-blue-955/20 dark:text-blue-450 border border-blue-100/40 dark:border-blue-900/30 shadow-sm">
              <FontAwesomeIcon icon={faCalendarAlt} className="w-3 h-3 text-blue-500/80 dark:text-blue-400/80" />
              Viewing data from <strong className="font-semibold">{data.filter.start_date}</strong> to <strong className="font-semibold">{data.filter.end_date}</strong>
            </span>
          )}
        </div>

        {/* Count Toggle */}
        <button
          type="button"
          onClick={() => setShowCounts(prev => !prev)}
          className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all duration-200 active:scale-95 border flex items-center gap-2 self-start shadow-sm ${
            showCounts
              ? 'bg-white border-gray-200/80 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-700'
              : 'bg-[var(--mainCol)] border-[var(--mainCol)] text-white hover:bg-opacity-95'
          }`}
        >
          <FontAwesomeIcon icon={showCounts ? faEyeSlash : faEye} />
          {showCounts ? 'Hide Counts' : 'Show Counts'}
        </button>
      </div>

      {/* 2. Filters Control Panel */}
      <form onSubmit={handleApplyFilter} className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm mb-6">
        <div className="flex flex-col md:flex-row md:items-end gap-4">

          {/* Time Range Filter */}
          <div className="flex-1 min-w-[200px]">
            <label className="block mb-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 flex items-center">
              <FontAwesomeIcon icon={faClock} className="mr-1 text-gray-400" />
              Time Range
            </label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-2 text-sm text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="this_month">This Month</option>
              <option value="last_month">Last Month</option>
              <option value="last_3_month">Last 3 Months</option>
              <option value="last_6_month">Last 6 Months</option>
              <option value="last_9_month">Last 9 Months</option>
              <option value="last_ytd">Year to Date (YTD)</option>
              <option value="custom">Custom Date Range</option>
            </select>
          </div>

          {/* Conditional Date Pickers for Custom range */}
          {filter === 'custom' && (
            <>
              {/* Start Date */}
              <div className="flex-1 min-w-[150px]">
                <label className="block mb-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 flex items-center">
                  <FontAwesomeIcon icon={faCalendarAlt} className="mr-1 text-gray-400" />
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-2 text-sm text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* End Date */}
              <div className="flex-1 min-w-[150px]">
                <label className="block mb-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 flex items-center">
                  <FontAwesomeIcon icon={faCalendarAlt} className="mr-1 text-gray-400" />
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-2 text-sm text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </>
          )}

          {/* Actions */}
          <div className="flex gap-2 min-w-[150px]">
            <button
              type="submit"
              className="activeBtn flex-1 text-white text-sm font-medium py-2 px-3 rounded-lg flex items-center justify-center hover:opacity-90 transition"
            >
              <FontAwesomeIcon icon={faFilter} className="mr-2" />
              Apply
            </button>
            <button
              type="button"
              onClick={handleResetFilter}
              className="bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium py-2 px-3 rounded-lg flex items-center justify-center transition"
              title="Reset Filters"
            >
              <FontAwesomeIcon icon={faUndo} />
            </button>
          </div>

        </div>
      </form>

      {/* 3. KPI Summary Metric Cards */}
      {/* <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">

  
        <div className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm border-l-4 border-blue-500 hover:-translate-y-0.5 hover:shadow transition-all duration-200">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">Redemptions Volume</p>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{formatNumber(Count.total_transactions)}</h3>
              <p className="text-xs text-blue-500 mt-2 font-medium flex items-center gap-1">
                <FontAwesomeIcon icon={faCoins} />
                {formatPoints(Value.total_points)} transacted
              </p>
            </div>
            <div className="p-2.5 bg-blue-50 dark:bg-blue-950/40 text-blue-500 rounded-lg">
              <FontAwesomeIcon icon={faExchangeAlt} className="w-5 h-5" />
            </div>
          </div>
        </div>


        <div className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm border-l-4 border-emerald-500 hover:-translate-y-0.5 hover:shadow transition-all duration-200">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">Welcome Bonuses</p>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{formatNumber(signup_POI.welcome_bonus_count)}</h3>
              <p className="text-xs text-emerald-500 mt-2 font-medium flex items-center gap-1">
                <FontAwesomeIcon icon={faCoins} />
                {formatPoints(signup_POI.welcome_bonus_points)} distributed
              </p>
            </div>
            <div className="p-2.5 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-500 rounded-lg">
              <FontAwesomeIcon icon={faGift} className="w-5 h-5" />
            </div>
          </div>
        </div>


        <div className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm border-l-4 border-yellow-500 hover:-translate-y-0.5 hover:shadow transition-all duration-200">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">Referrer Rewards</p>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{formatNumber(signup_POI.referrer_bonus_count)}</h3>
              <p className="text-xs text-yellow-500 mt-2 font-medium flex items-center gap-1">
                <FontAwesomeIcon icon={faCoins} />
                {formatPoints(signup_POI.referrer_bonus_points)} rewarded
              </p>
            </div>
            <div className="p-2.5 bg-yellow-50 dark:bg-yellow-950/40 text-yellow-500 rounded-lg">
              <FontAwesomeIcon icon={faUserPlus} className="w-5 h-5" />
            </div>
          </div>
        </div>


        <div className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm border-l-4 border-purple-500 hover:-translate-y-0.5 hover:shadow transition-all duration-200">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">Invited Rewards</p>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{formatNumber(signup_POI.refer_bonus_count)}</h3>
              <p className="text-xs text-purple-500 mt-2 font-medium flex items-center gap-1">
                <FontAwesomeIcon icon={faCoins} />
                {formatPoints(signup_POI.refer_bonus_points)} rewarded
              </p>
            </div>
            <div className="p-2.5 bg-purple-50 dark:bg-purple-950/40 text-purple-500 rounded-lg">
              <FontAwesomeIcon icon={faShareAlt} className="w-5 h-5" />
            </div>
          </div>
        </div>

      </div> */}

      {/* Total Distribution Summary */}
      <div className="w-full bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm mb-5">
        <h3 className="text-md font-bold text-gray-800 dark:text-white mb-3">Total Distribution</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data?.total_distribution?.map((elem, idx) => {
            const secondaryStats = Object.entries(elem).filter(
              ([key]) => !['title', 'points', 'info'].includes(key)
            );
            const isRedeem = elem.title?.toLowerCase().includes('redeem');
            const borderColorClass = isRedeem ? 'border-l-4 border-emerald-500' : 'border-l-4 border-blue-500';
            const iconColorClass = isRedeem ? 'text-emerald-500 bg-emerald-50 dark:bg-emerald-950/40' : 'text-blue-500 bg-blue-50 dark:bg-blue-950/40';

            const formatKey = (key) => {
              return key
                .replace(/_/g, ' ')
                .replace(/\b\w/g, (char) => char.toUpperCase());
            };

            return (
              <div
                key={idx}
                className={`bg-gray-50 dark:bg-gray-700/40 p-4 rounded-xl border border-gray-150 dark:border-gray-700 shadow-sm flex flex-col justify-between hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 ${borderColorClass}`}
              >
                <div>
                  <div className="flex justify-between items-start gap-3">
                    <div>
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <p className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400">{elem.title}</p>
                        {elem.info && (
                          <div className="relative group/info flex items-center">
                            <FontAwesomeIcon icon={faInfoCircle} className="w-2.5 h-2.5 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 cursor-help" />
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-52 hidden group-hover/info:block bg-gray-900 text-white text-[11px] p-2.5 rounded-lg shadow-xl z-30 border border-gray-800 leading-relaxed font-normal normal-case tracking-normal">
                              {elem.info}
                              <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                            </div>
                          </div>
                        )}
                      </div>
                      <h4 className="text-xl font-black text-gray-900 dark:text-white">{formatPoints(elem.points)}</h4>
                    </div>
                    <div className={`p-2 rounded-lg ${iconColorClass}`}>
                      <FontAwesomeIcon icon={isRedeem ? faExchangeAlt : faCoins} className="w-4 h-4" />
                    </div>
                  </div>
                </div>

                {secondaryStats.length > 0 && showCounts && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3 pt-3 border-t border-gray-250 dark:border-gray-700">
                    {secondaryStats.map(([key, val]) => {
                      let valColor = 'text-gray-800 dark:text-gray-200';
                      if (key.includes('complete')) valColor = 'text-emerald-600 dark:text-emerald-450';
                      if (key.includes('pending')) valColor = 'text-yellow-600 dark:text-yellow-450';
                      if (key.includes('fail')) valColor = 'text-red-600 dark:text-red-450';

                      return (
                        <div key={key} className="bg-white dark:bg-gray-800/85 p-1.5 rounded-lg text-center border border-gray-100 dark:border-gray-750 shadow-inner">
                          <span className="block text-[9px] font-bold text-gray-450 uppercase tracking-wide truncate" title={formatKey(key)}>
                            {formatKey(key)}
                          </span>
                          <span className={`text-xs font-bold block mt-0.5 ${valColor}`}>
                            {formatNumber(val)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Signup POI Distribution Summary */}
      {data?.signup_POI_distribution && data.signup_POI_distribution.length > 0 && (
        <div className="w-full bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm mb-5">
          <h3 className="text-md font-bold text-gray-800 dark:text-white mb-3">Signup POI Distribution</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {data.signup_POI_distribution.map((elem, idx) => {
              const secondaryStats = Object.entries(elem).filter(
                ([key]) => !['title', 'Point', 'points', 'info'].includes(key)
              );

              const pts = elem.Point !== undefined ? elem.Point : (elem.points !== undefined ? elem.points : 0);

              const cardThemes = [
                'border-l-4 border-indigo-500 bg-indigo-50/10 dark:bg-indigo-950/5',
                'border-l-4 border-emerald-500 bg-emerald-50/10 dark:bg-emerald-950/5',
                'border-l-4 border-yellow-500 bg-yellow-50/10 dark:bg-yellow-950/5',
                'border-l-4 border-purple-500 bg-purple-50/10 dark:bg-purple-950/5',
              ];
              const themeClass = cardThemes[idx % cardThemes.length];

              const formatKey = (key) => {
                return key
                  .replace(/_/g, ' ')
                  .replace(/\b\w/g, (char) => char.toUpperCase());
              };

              return (
                <div
                  key={idx}
                  className={`p-4 rounded-xl border border-gray-150 dark:border-gray-700 shadow-sm flex flex-col justify-between hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 ${themeClass}`}
                >
                  <div>
                    <div className="flex justify-between items-start gap-2">
                      <div>
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <p className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400">{elem.title}</p>
                          {elem.info && (
                            <div className="relative group/info flex items-center">
                              <FontAwesomeIcon icon={faInfoCircle} className="w-2.5 h-2.5 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-355 cursor-help" />
                              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-52 hidden group-hover/info:block bg-gray-900 text-white text-[11px] p-2.5 rounded-lg shadow-xl z-30 border border-gray-800 leading-relaxed font-normal normal-case tracking-normal">
                                {elem.info}
                                <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                              </div>
                            </div>
                          )}
                        </div>
                        <h4 className="text-lg font-black text-gray-900 dark:text-white">{formatPoints(pts)}</h4>
                      </div>
                      <div className="p-1.5 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700">
                        <FontAwesomeIcon icon={faCoins} className="w-3.5 h-3.5 text-blue-500" />
                      </div>
                    </div>
                  </div>

                  {secondaryStats.length > 0 && showCounts && (
                    <div className="grid grid-cols-1 gap-1.5 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                      {secondaryStats.map(([key, val]) => (
                        <div key={key} className="flex justify-between items-center bg-white/90 dark:bg-gray-800/90 px-2 py-1 rounded-lg border border-gray-100 dark:border-gray-750 shadow-inner">
                          <span className="text-[9px] font-bold text-gray-450 uppercase tracking-wide truncate" title={formatKey(key)}>
                            {formatKey(key)}
                          </span>
                          <span className="text-xs font-bold text-gray-800 dark:text-gray-200">
                            {formatNumber(val)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Redeem Distribution Summary */}
      {data?.redeem_distribution && data.redeem_distribution.length > 0 && (
        <div className="w-full bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm mb-5">
          <h3 className="text-md font-bold text-gray-800 dark:text-white mb-3">Redeem Distribution</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.redeem_distribution.map((elem, idx) => {
              const secondaryStats = Object.entries(elem).filter(
                ([key]) => !['title', 'Point', 'points', 'info'].includes(key)
              );

              const pts = elem.Point !== undefined ? elem.Point : (elem.points !== undefined ? elem.points : 0);

              const cardThemes = [
                'border-l-4 border-rose-500 bg-rose-50/10 dark:bg-rose-950/5',
                'border-l-4 border-teal-500 bg-teal-50/10 dark:bg-teal-950/5',
                'border-l-4 border-cyan-500 bg-cyan-50/10 dark:bg-cyan-950/5',
              ];
              const themeClass = cardThemes[idx % cardThemes.length];

              const formatKey = (key) => {
                return key
                  .replace(/_/g, ' ')
                  .replace(/\b\w/g, (char) => char.toUpperCase());
              };

              return (
                <div
                  key={idx}
                  className={`p-4 rounded-xl border border-gray-150 dark:border-gray-700 shadow-sm flex flex-col justify-between hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 ${themeClass}`}
                >
                  <div>
                    <div className="flex justify-between items-start gap-2">
                      <div>
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <p className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400">{elem.title}</p>
                          {elem.info && (
                            <div className="relative group/info flex items-center">
                              <FontAwesomeIcon icon={faInfoCircle} className="w-2.5 h-2.5 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-355 cursor-help" />
                              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-52 hidden group-hover/info:block bg-gray-900 text-white text-[11px] p-2.5 rounded-lg shadow-xl z-30 border border-gray-800 leading-relaxed font-normal normal-case tracking-normal">
                                {elem.info}
                                <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                              </div>
                            </div>
                          )}
                        </div>
                        <h4 className="text-lg font-black text-gray-900 dark:text-white">{formatPoints(pts)}</h4>
                      </div>
                      <div className="p-1.5 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700">
                        <FontAwesomeIcon icon={faCoins} className="w-3.5 h-3.5 text-blue-500" />
                      </div>
                    </div>
                  </div>

                  {secondaryStats.length > 0 && showCounts && (
                    <div className="grid grid-cols-1 gap-1.5 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                      {secondaryStats.map(([key, val]) => (
                        <div key={key} className="flex justify-between items-center bg-white/90 dark:bg-gray-800/90 px-2 py-1 rounded-lg border border-gray-100 dark:border-gray-750 shadow-inner">
                          <span className="text-[9px] font-bold text-gray-450 uppercase tracking-wide truncate" title={formatKey(key)}>
                            {formatKey(key)}
                          </span>
                          <span className="text-xs font-bold text-gray-800 dark:text-gray-200">
                            {formatNumber(val)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {loading && (
        <div className="w-full py-4 flex justify-center">
          <Loader />
        </div>
      )}

      {/*

  {viewMode === 'charts' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">

          <div className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm flex flex-col items-center">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 self-start">Redemptions by Category (Frequency)</h4>
            <div className="w-full relative h-[250px] flex justify-center">
              {categoryLabels.length > 0 ? (
                <Pie data={countChartData} options={chartOptions} />
              ) : (
                <p className="text-sm text-gray-400 my-auto">No transaction data matching this criteria.</p>
              )}
            </div>
          </div>


          <div className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm flex flex-col items-center">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 self-start">Redemptions by Category (Points Value)</h4>
            <div className="w-full relative h-[250px] flex justify-center">
              {categoryLabels.length > 0 ? (
                <Doughnut data={valueChartData} options={chartOptions} />
              ) : (
                <p className="text-sm text-gray-400 my-auto">No transaction data matching this criteria.</p>
              )}
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm flex flex-col">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Signup Points Distribution (Points Value)</h4>
            <div className="w-full relative h-[250px]">
              <Bar data={poiPointsChartData} options={barChartOptions} />
            </div>
          </div>

  
          <div className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm flex flex-col">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Signup Distributions (Frequency)</h4>
            <div className="w-full relative h-[250px]">
              <Bar data={poiCountsChartData} options={barChartOptions} />
            </div>
          </div>

        </div>

      ) : (

        <div className="space-y-6">

          <div className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Transaction Categories Summary</h4>
            <div className="w-full overflow-x-auto">
              <table className="w-full text-sm text-left border-collapse">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-gray-700 text-gray-400 text-xs font-semibold uppercase tracking-wider">
                    <th className="py-3 px-4">Transaction Type</th>
                    <th className="py-3 px-4 text-center">Frequency (Count)</th>
                    <th className="py-3 px-4 text-right">Points Transacted</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {title_wise.length > 0 ? (
                    title_wise.map((item, idx) => (
                      <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition">
                        <td className="py-3.5 px-4 font-medium">{item.title}</td>
                        <td className="py-3.5 px-4 text-center">{formatNumber(item.transaction_count)}</td>
                        <td className="py-3.5 px-4 text-right font-semibold text-blue-500">{formatPoints(item.total_points)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" className="text-center py-6 text-gray-400">No category statistics available.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>


          <div className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Transaction Status Wise Breakdown</h4>
            <div className="w-full overflow-x-auto">
              <table className="w-full text-sm text-left border-collapse">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-gray-700 text-gray-400 text-xs font-semibold uppercase tracking-wider">
                    <th className="py-3 px-4">Transaction Status</th>
                    <th className="py-3 px-4 text-center">Frequency (Count)</th>
                    <th className="py-3 px-4 text-right">Points Transacted</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {status_wise.length > 0 ? (
                    status_wise.map((item, idx) => (
                      <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition">
                        <td className="py-3.5 px-4 font-medium flex items-center gap-2">
                          <span className={`w-2.5 h-2.5 rounded-full ${item.transactionStatus === 'Complete' ? 'bg-emerald-500' :
                            item.transactionStatus === 'Pending' ? 'bg-yellow-500' : 'bg-red-500'
                            }`}></span>
                          {item.transactionStatus}
                        </td>
                        <td className="py-3.5 px-4 text-center">{formatNumber(item.transaction_count)}</td>
                        <td className="py-3.5 px-4 text-right font-semibold text-blue-500">{formatPoints(item.total_points)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" className="text-center py-6 text-gray-400">No status statistics available.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>


          <div className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Signup and Invite Referral Rewards Detail</h4>
            <div className="w-full overflow-x-auto">
              <table className="w-full text-sm text-left border-collapse">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-gray-700 text-gray-400 text-xs font-semibold uppercase tracking-wider">
                    <th className="py-3 px-4">Bonus Category</th>
                    <th className="py-3 px-4 text-center">Distributed Count</th>
                    <th className="py-3 px-4 text-right">Points Distributed</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition">
                    <td className="py-3.5 px-4 font-medium">Welcome Sign-up Reward</td>
                    <td className="py-3.5 px-4 text-center">{formatNumber(signup_POI.welcome_bonus_count)}</td>
                    <td className="py-3.5 px-4 text-right font-semibold text-purple-500">{formatPoints(signup_POI.welcome_bonus_points)}</td>
                  </tr>
                  <tr className="hover:bg-gray-55/40 dark:hover:bg-gray-700/30 transition">
                    <td className="py-3.5 px-4 font-medium">Inviter (Referrer) Reward</td>
                    <td className="py-3.5 px-4 text-center">{formatNumber(signup_POI.referrer_bonus_count)}</td>
                    <td className="py-3.5 px-4 text-right font-semibold text-purple-500">{formatPoints(signup_POI.referrer_bonus_points)}</td>
                  </tr>
                  <tr className="hover:bg-gray-55/40 dark:hover:bg-gray-700/30 transition">
                    <td className="py-3.5 px-4 font-medium">Invitee (Referral) Reward</td>
                    <td className="py-3.5 px-4 text-center">{formatNumber(signup_POI.refer_bonus_count)}</td>
                    <td className="py-3.5 px-4 text-right font-semibold text-purple-500">{formatPoints(signup_POI.refer_bonus_points)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

        </div>
      )}

*/}
    </div>
  );
}

export default Stats;